import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { BackButton } from '../components/back-button'
import { Spinner, apiClient, useCurrentUser } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen } from '../components/mobile-screen'
import { useConversationMessages } from '../_lib/messaging-api'

export function MessageThreadPage() {
  const { conversationId } = useParams<{ conversationId: string }>()
  const { user } = useCurrentUser()
  const { data, isLoading, refetch } = useConversationMessages(conversationId)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [data])

  const onSend = async () => {
    if (!draft.trim() || !conversationId) return
    setBusy(true)
    setError(null)
    try {
      // gapi-restful-regroup:conversation_id 走 path;字段名 content(不是 body)
      await apiClient.post(`/gapi/user/conversations/${conversationId}/messages`, {
        content: draft,
      })
      setDraft('')
      await refetch()
    } catch (e) {
      setError(e instanceof Error ? e.message : '发送失败')
    } finally {
      setBusy(false)
    }
  }

  const messages = data?.messages ?? []
  const peer = messages[0]?.sender_id !== user?.user_id ? messages[0]?.sender_id : null

  return (
    <AppLayout>
      <MobileScreen bg="surface">
        <header data-testid="DlALe" className="flex items-center justify-between gap-3 border-b border-[#F3F4F6] bg-white px-6 pt-[44px] pb-3">
          <BackButton fallback="/messages" className="shrink-0" />
          <h1 className="flex-1 text-center text-[16px] font-semibold text-[#1A1A1A]">对话详情</h1>
          <button type="button" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F6F7F8]">
            <svg width="4" height="16" viewBox="0 0 4 16" fill="#1A1A1A"><circle cx="2" cy="2" r="1.5" /><circle cx="2" cy="8" r="1.5" /><circle cx="2" cy="14" r="1.5" /></svg>
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading ? (
            <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
          ) : (
            <ul className="flex flex-col gap-3">
              {messages.map((m) => {
                const mine = m.sender_id === user?.user_id
                return (
                  <li key={m.id} className={`flex items-end gap-2 ${mine ? 'flex-row-reverse' : 'flex-row'}`}>
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold text-white ${mine ? 'bg-[#1971ca]' : 'bg-[#19caad]'}`}
                    >
                      {(mine ? '我' : peer ?? '对').slice(0, 1)}
                    </span>
                    <div className="max-w-[70%]">
                      <div className="mb-0.5 flex items-center gap-1 text-[10px] text-[#9CA3AF]">
                        <span>{mine ? '我' : '对方'}</span>
                        <span>{m.created_at.slice(11, 16)}</span>
                      </div>
                      <div
                        className={
                          mine
                            ? 'rounded-2xl rounded-br-md bg-[#19caad] px-3 py-2 text-[13px] text-white'
                            : 'rounded-2xl rounded-bl-md bg-white px-3 py-2 text-[13px] text-[#1A1A1A]'
                        }
                      >
                        {m.content}
                      </div>
                    </div>
                  </li>
                )
              })}
              <div ref={endRef} />
            </ul>
          )}
        </div>

        <footer
          className="border-t border-[#F3F4F6] bg-white px-4 pt-3"
          style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        >
          {error && <p className="mb-1 text-[12px] text-[#FF6B6B]">{error}</p>}
          <p className="mb-2 text-[11px] text-[#9CA3AF]">回复此对话</p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="输入回复内容…"
            rows={3}
            className="mb-2 w-full resize-none rounded-lg bg-[#F6F7F8] p-3 text-[13px] outline-none placeholder:text-[#9CA3AF]"
          />
          <div className="flex items-center justify-between">
            <button type="button" className="flex items-center gap-1 text-[12px] text-[#9CA3AF]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12V8a2 2 0 00-2-2h-3l-2-2H10L8 6H5a2 2 0 00-2 2v10a2 2 0 002 2h7" /><circle cx="17" cy="17" r="3" /></svg>
              附件
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={busy || !draft.trim()}
              className="flex items-center gap-1 rounded-lg bg-[#19caad] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-50"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
              发送
            </button>
          </div>
        </footer>
      </MobileScreen>
    </AppLayout>
  )
}
